import { useState } from 'react';
import {
  validateCredentials,
  validateContactEmail,
} from '@/lib/auth-validation';
import type { UserRole } from '@/types/index';
import TermsCheckbox from './TermsCheckbox';
import { Button } from '@/components/ui/Button';

/**
 * Username + password auth form, used by both login and register pages.
 *
 * Exports `MethodPicker` (the small Passkey | Password segmented control
 * shown above the form) so pages can render the toggle without dragging in
 * a second file. Pure presentational + local form state — the parent owns
 * the mutation and the post-success navigation.
 */

const MIN_PASSWORD_LEN = 12;

export type AuthMethodChoice = 'passkey' | 'password';

export function MethodPicker({
  value,
  onChange,
  disabled,
}: {
  value: AuthMethodChoice;
  onChange: (v: AuthMethodChoice) => void;
  disabled?: boolean;
}) {
  const choices: Array<{ id: AuthMethodChoice; label: string }> = [
    { id: 'passkey', label: 'Passkey' },
    { id: 'password', label: 'Username + Password' },
  ];
  return (
    <div
      role="tablist"
      aria-label="Authentication method"
      className="flex w-full rounded-[--radi-6] border border-[--base-border] bg-[--base-surface-1] p-[--spacing-3]"
    >
      {choices.map((c) => {
        const active = c.id === value;
        return (
          <button
            key={c.id}
            type="button"
            role="tab"
            aria-selected={active}
            disabled={disabled}
            onClick={() => onChange(c.id)}
            className="flex-1 font-medium transition-opacity disabled:opacity-50"
            style={{
              height: '36px',
              fontSize: '14px',
              borderRadius: '8px',
              border: 'none',
              cursor: disabled ? 'not-allowed' : 'pointer',
              backgroundColor: active
                ? 'var(--state-brand-active, #36d399)'
                : 'transparent',
              color: active
                ? 'var(--text-light-primary, #141414)'
                : 'var(--text-dark-secondary, rgba(255,255,255,0.7))',
            }}
          >
            {c.label}
          </button>
        );
      })}
    </div>
  );
}

export interface PasswordFormValues {
  username: string;
  contactEmail?: string;
  name?: string;
  password: string;
}

export default function PasswordAuthForm({
  mode,
  role,
  onSubmit,
  onOpenTerms,
  initialValues,
  submitText,
  collectName = true,
}: {
  mode: 'login' | 'register';
  role: UserRole;
  onSubmit: (values: PasswordFormValues) => Promise<void>;
  onOpenTerms?: () => void;
  initialValues?: PasswordFormValues;
  submitText?: string;
  collectName?: boolean;
}) {
  const [username, setUsername] = useState(initialValues?.username ?? '');
  const [name, setName] = useState(initialValues?.name ?? '');
  const [contactEmail, setContactEmail] = useState(
    initialValues?.contactEmail ?? ''
  );
  const [password, setPassword] = useState(initialValues?.password ?? '');
  const [confirmPassword, setConfirmPassword] = useState(
    initialValues?.password ?? ''
  );
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    try {
      validateCredentials(
        username,
        password,
        mode === 'register' && collectName ? name : undefined
      );
      if (mode === 'register' && role === 'client' && contactEmail)
        validateContactEmail(contactEmail);
    } catch (reason) {
      return setError(
        reason instanceof Error ? reason.message : 'Invalid credentials.'
      );
    }
    if (mode === 'register' && password !== confirmPassword) {
      return setError('Passwords do not match.');
    }
    if (mode === 'register' && !acceptedTerms) {
      return setError(
        'Debe aceptar los términos y condiciones para continuar.'
      );
    }

    setBusy(true);
    try {
      await onSubmit({
        username,
        password,
        ...(mode === 'register' && collectName ? { name } : {}),
        ...(mode === 'register' && role === 'client' ? { contactEmail } : {}),
      });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Something went wrong, please try again.'
      );
    } finally {
      setPassword('');
      setConfirmPassword('');
      setBusy(false);
    }
  };

  const submitLabel = submitText
    ? busy
      ? 'Continuing…'
      : submitText
    : mode === 'login'
      ? busy
        ? 'Logging in…'
        : 'Login'
      : busy
        ? 'Creating account…'
        : 'Create account';

  return (
    <form onSubmit={handleSubmit} aria-label={`${mode} as ${role}`}>
      {mode === 'register' && collectName && (
        <div className="inputGroup">
          <label htmlFor="name" className="block">
            Full name
          </label>
          <input
            id="name"
            type="text"
            name="name"
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            disabled={busy}
          />
        </div>
      )}

      <div>
        <label htmlFor="username" className="block">
          Username
        </label>
        <input
          id="username"
          type="text"
          name="username"
          placeholder="your_username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
          disabled={busy}
          autoComplete="username"
        />
      </div>

      {mode === 'register' && role === 'client' && (
        <div className="inputGroup">
          <label htmlFor="contactEmail">Email (contact, optional)</label>
          <input
            id="contactEmail"
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            autoComplete="email"
            disabled={busy}
          />
        </div>
      )}

      <div className="inputGroup">
        <label htmlFor="password" className="block">
          Password
        </label>
        <input
          id="password"
          type="password"
          name="password"
          placeholder={`At least ${MIN_PASSWORD_LEN} UTF-8 bytes`}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={mode === 'register' ? 3 : 1}
          disabled={busy}
          autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
        />
      </div>

      {mode === 'register' && (
        <div className="inputGroup">
          <label htmlFor="confirmPassword" className="block">
            Confirm password
          </label>
          <input
            id="confirmPassword"
            type="password"
            name="confirmPassword"
            placeholder="Repeat your password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            disabled={busy}
            autoComplete="new-password"
          />
        </div>
      )}

      {error && (
        <p style={{ fontSize: '14px', color: '#ef4444' }} role="alert">
          {error}
        </p>
      )}

      {mode === 'register' && (
        <TermsCheckbox
          checked={acceptedTerms}
          onChange={setAcceptedTerms}
          onOpenTerms={onOpenTerms || (() => {})}
          disabled={busy}
        />
      )}
      <Button
        type="submit"
        disabled={busy || (mode === 'register' && !acceptedTerms)}
      >
        {submitLabel}
      </Button>

      {busy && !submitText && (
        <p
          className="text-center"
          style={{
            fontSize: '14px',
            color: 'var(--text-dark-secondary, rgba(255,255,255,0.7))',
          }}
        >
          Contacting the server…
        </p>
      )}
    </form>
  );
}
